import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addNewAdherent,
  deleteAdherent,
  getAllAdherent,
  updateAdherent,
} from "../../../../../services/adherentServices";
import { getGroupsHome } from "../../../../../services/groupServices";

import {
  buildGroupColorMap,
  buildInscriptionsStats,
  exportAdherentsToExcel,
  exportAdherentsToPDF,
  filterAndSortAdherents,
  isDemoUser,
  normalizeAdherentPayload,
  safeJsonParse,
} from "../utils/inscriptionsUtils";

const INITIAL_FILTERS = {
  query: "",
  group: "",
  sortOrder: "name_asc",
};

export default function useInscriptions() {
  const [adherents, setAdherents] = useState([]);
  const [groups, setGroups] = useState([]);

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedAdherent, setSelectedAdherent] = useState(null);

  const [deleteCandidateId, setDeleteCandidateId] = useState(null);

  const [isDemoMode, setIsDemoMode] = useState(false);

  const setFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const refreshData = useCallback(async () => {
    setLoading(true);

    try {
      const [adherentsData, groupsData] = await Promise.all([
        getAllAdherent(),
        getGroupsHome(),
      ]);

      setAdherents(Array.isArray(adherentsData) ? adherentsData : []);
      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch (error) {
      console.error("Erreur lors du chargement des inscriptions :", error);
      toast.error("Erreur lors de la récupération des adhérents.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserMode();
    refreshData();
  }, [loadUserMode, refreshData]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const groupColorMap = useMemo(() => buildGroupColorMap(groups), [groups]);

  const filteredAdherents = useMemo(
    () => filterAndSortAdherents(adherents, filters),
    [adherents, filters]
  );

  const stats = useMemo(
    () =>
      buildInscriptionsStats({
        adherents,
        filteredAdherents,
        groups,
      }),
    [adherents, filteredAdherents, groups]
  );

  const openCreateDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedAdherent(null);
    setDrawerMode("create");
  }, [guardDemoMode]);

  const openEditDrawer = useCallback(
    (adherent) => {
      if (guardDemoMode()) return;

      setSelectedAdherent(adherent);
      setDrawerMode("edit");
    },
    [guardDemoMode]
  );

  const openDetailsDrawer = useCallback((adherent) => {
    setSelectedAdherent(adherent);
    setDrawerMode("details");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedAdherent(null);
  }, []);

  const createAdherent = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeAdherentPayload(formData);

        await addNewAdherent(payload);

        toast.success("Adhérent créé avec succès.");
        closeDrawer();
        await refreshData();
      } catch (error) {
        console.error("Erreur lors de la création de l'adhérent :", error);
        toast.error("Erreur lors de la création de l'adhérent.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshData]
  );

  const saveAdherent = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeAdherentPayload(formData);

        await updateAdherent(payload.id, payload);

        toast.success("Adhérent modifié avec succès.");
        closeDrawer();
        await refreshData();
      } catch (error) {
        console.error("Erreur lors de la modification de l'adhérent :", error);
        toast.error("Erreur lors de la modification de l'adhérent.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshData]
  );

  const askDelete = useCallback(
    (adherentId) => {
      if (guardDemoMode()) return;
      setDeleteCandidateId(adherentId);
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidateId(null);
  }, []);

  const confirmDelete = useCallback(
    async (adherentId) => {
      if (guardDemoMode()) return;

      try {
        await deleteAdherent(adherentId);

        toast.success("Adhérent supprimé avec succès.");
        setDeleteCandidateId(null);
        await refreshData();
      } catch (error) {
        console.error("Erreur lors de la suppression de l'adhérent :", error);
        toast.error("Erreur lors de la suppression de l'adhérent.");
      }
    },
    [guardDemoMode, refreshData]
  );

  const toggleSortByName = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      sortOrder: prev.sortOrder === "name_asc" ? "name_desc" : "name_asc",
    }));
  }, []);

  const exportExcel = useCallback(async () => {
    setExporting(true);

    try {
      exportAdherentsToExcel(filteredAdherents);
      toast.success("Export Excel généré avec succès.");
    } catch (error) {
      console.error("Erreur lors de l'export Excel :", error);
      toast.error("Erreur lors de l'export Excel.");
    } finally {
      setExporting(false);
    }
  }, [filteredAdherents]);

  const exportPDF = useCallback(async () => {
    setExporting(true);

    try {
      exportAdherentsToPDF(filteredAdherents, filters.group);
      toast.success("PDF généré avec succès.");
    } catch (error) {
      console.error("Erreur lors de l'export PDF :", error);
      toast.error("Erreur lors de l'export PDF.");
    } finally {
      setExporting(false);
    }
  }, [filteredAdherents, filters.group]);

  return {
    loading,
    saving,
    exporting,
    filters,
    setFilter,
    adherents,
    filteredAdherents,
    groups,
    groupColorMap,
    selectedAdherent,
    drawerMode,
    deleteCandidateId,
    stats,
    actions: {
      refreshData,
      openCreateDrawer,
      openEditDrawer,
      openDetailsDrawer,
      closeDrawer,
      createAdherent,
      updateAdherent: saveAdherent,
      askDelete,
      cancelDelete,
      confirmDelete,
      toggleSortByName,
      exportExcel,
      exportPDF,
    },
  };
}